import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Alert,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Paper,
  Divider,
  Chip,
  ListSubheader
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import api from '../../services/api';
import { isGlobalAdmin, isTenantScopedAdmin } from '../../utils/role';

const normalizeId = (value) => {
  if (!value) {
    return null;
  }
  try {
    return value.toString();
  } catch {
    return value;
  }
};

const PresetManager = ({ type, title }) => {
  const [presets, setPresets] = useState({ items: [], ownerType: null, ownerId: null });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [dialog, setDialog] = useState({
    open: false,
    preset: null,
    isEditing: false
  });
  const [saving, setSaving] = useState(false);

  const currentUser = useMemo(() => {
    try {
      const raw = localStorage.getItem('user');
      return raw ? JSON.parse(raw) : null;
    } catch (err) {
      console.error('Failed to parse stored user', err);
      return null;
    }
  }, []);

  const canManageGlobal = isGlobalAdmin(currentUser) && !currentUser?.universityId;
  const canManageTenant = isTenantScopedAdmin(currentUser) && Boolean(currentUser?.universityId);
  const userUniversityId = normalizeId(currentUser?.universityId);

  const getEndpoint = () => `/platform/presets/${type}`;

  useEffect(() => {
    loadPresets();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type]);

  const loadPresets = async () => {
    try {
      setLoading(true);
      const response = await api.get(getEndpoint());
      const payload = response.data || {};

      const items = Array.isArray(payload.items)
        ? payload.items
        : Array.isArray(payload.all)
          ? payload.all
          : Array.isArray(payload)
            ? payload
            : [];

      setPresets({
        items: items.map((item) => ({
          ...item,
          ownerType: item.ownerType || payload.ownerType || null,
          ownerId: normalizeId(item.ownerId)
        })),
        ownerType: payload.ownerType || (currentUser?.universityId ? 'university' : 'virtual-medic'),
        ownerId: normalizeId(payload.ownerId)
      });
    } catch (err) {
      console.error('Error loading presets:', err);
      setError(`Failed to load ${title.toLowerCase()}`);
      setPresets({ items: [], ownerType: null, ownerId: null });
    } finally {
      setLoading(false);
    }
  };

  const ownerType = presets.ownerType || (currentUser?.universityId ? 'university' : 'virtual-medic');
  const ownerLabel = ownerType === 'virtual-medic' ? 'Virtual Medic defaults' : 'University presets';
  const canCreate = ownerType === 'virtual-medic' ? canManageGlobal : (canManageTenant || canManageGlobal);

  const canManagePreset = (preset) => {
    const presetOwnerType = preset.ownerType || ownerType;
    const presetOwnerId = normalizeId(preset.ownerId);

    if (presetOwnerType === 'virtual-medic') {
      return canManageGlobal;
    }

    if (canManageGlobal) {
      return true;
    }

    return canManageTenant && presetOwnerId && userUniversityId && presetOwnerId === userUniversityId;
  };

  const openCreateDialog = () => {
    if (!canCreate) {
      return;
    }

    setDialog({
      open: true,
      preset: {
        name: ''
      },
      isEditing: false
    });
  };

  const openEditDialog = (preset) => {
    setDialog({
      open: true,
      preset: { ...preset },
      isEditing: true
    });
  };

  const closeDialog = () => {
    setDialog({ open: false, preset: null, isEditing: false });
    setError('');
  };

  const savePreset = async () => {
    try {
      setSaving(true);
      setError('');

      if (!dialog.preset.name.trim()) {
        setError('Name is required');
        return;
      }

      const data = {
        name: dialog.preset.name.trim()
      };

      const method = dialog.isEditing ? 'put' : 'post';
      const url = dialog.isEditing
        ? `${getEndpoint()}/${dialog.preset._id}`
        : getEndpoint();

      await api[method](url, data);

      closeDialog();
      loadPresets();
    } catch (err) {
      console.error('Error saving preset:', err);
      setError(err.response?.data?.message || `Failed to save ${title.toLowerCase().slice(0, -1)}`);
    } finally {
      setSaving(false);
    }
  };

  const deletePreset = async (presetId) => {
    if (!window.confirm(`Are you sure you want to delete this ${title.toLowerCase().slice(0, -1)}? This action cannot be undone.`)) {
      return;
    }

    try {
      await api.delete(`${getEndpoint()}/${presetId}`);
      loadPresets();
    } catch (err) {
      console.error('Error deleting preset:', err);
      setError(`Failed to delete ${title.toLowerCase().slice(0, -1)}`);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" py={4}>
        <CircularProgress />
      </Box>
    );
  }

  const hasAnyPresets = (presets.items || []).length > 0;

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h5">{title} Management</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={openCreateDialog}
          disabled={!canCreate}
          sx={{
            bgcolor: canCreate ? '#16a085' : 'action.disabledBackground',
            '&:hover': { bgcolor: canCreate ? '#1abc9c' : 'action.disabled' }
          }}
        >
          Add {title.slice(0, -1)}
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <Paper>
        {hasAnyPresets ? (
          <List>
            <ListSubheader disableSticky sx={{ bgcolor: 'transparent', px: 2 }}>
              {ownerLabel}
            </ListSubheader>
            {presets.items.map((preset, index) => {
              const canEdit = canManagePreset(preset);
              const createdDate = preset.createdAt ? new Date(preset.createdAt).toLocaleDateString() : 'Unknown';
              const presetOwnerType = preset.ownerType || ownerType;
              const chipLabel = presetOwnerType === 'virtual-medic' ? 'Virtual Medic' : 'University';

              return (
                <React.Fragment key={preset._id}>
                  <ListItem alignItems="center">
                    <ListItemText
                      primary={preset.name}
                      secondary={`Created: ${createdDate}`}
                    />
                    <ListItemSecondaryAction>
                      <Box display="flex" alignItems="center" gap={1}>
                        <Chip
                          size="small"
                          label={chipLabel}
                          color={presetOwnerType === 'virtual-medic' ? 'default' : 'primary'}
                          variant={presetOwnerType === 'virtual-medic' ? 'outlined' : 'filled'}
                        />
                        <IconButton
                          edge="end"
                          onClick={() => openEditDialog(preset)}
                          sx={{ mr: 1 }}
                          disabled={!canEdit}
                        >
                          <EditIcon />
                        </IconButton>
                        <IconButton
                          edge="end"
                          color="error"
                          onClick={() => deletePreset(preset._id)}
                          disabled={!canEdit}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Box>
                    </ListItemSecondaryAction>
                  </ListItem>
                  {index < presets.items.length - 1 && <Divider component="li" />}
                </React.Fragment>
              );
            })}
          </List>
        ) : (
          <Box textAlign="center" py={4}>
            <Typography variant="body1" color="text.secondary" gutterBottom>
              No {title.toLowerCase()} found
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Add {title.toLowerCase()} that instructors can use when creating cases
            </Typography>
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={openCreateDialog}
              disabled={!canCreate}
            >
              Add Your First {title.slice(0, -1)}
            </Button>
          </Box>
        )}
      </Paper>

      <Dialog open={dialog.open} onClose={closeDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {dialog.isEditing ? `Edit ${title.slice(0, -1)}` : `Add New ${title.slice(0, -1)}`}
        </DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Name"
            value={dialog.preset?.name || ''}
            onChange={(e) => setDialog({
              ...dialog,
              preset: { ...dialog.preset, name: e.target.value }
            })}
            required
            sx={{ mt: 2 }}
            helperText={`Name of the ${title.toLowerCase().slice(0, -1)}`}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog}>Cancel</Button>
          <Button
            onClick={savePreset}
            variant="contained"
            disabled={saving}
          >
            {saving ? 'Saving...' : (dialog.isEditing ? 'Update' : 'Create')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PresetManager;
